import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import UserMasterService from "@/api/userMasterService";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching user list.
 * It utilizes the `useQuery` hook to retrieve user data with pagination and filters.
 *
 * @param {Object} params - Query parameters (page, limit, search, status, admin_status, signup_type, sort_by, order_by)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListUser = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["user-list", params], // Query key
    queryFn: () => {
      return UserMasterService.getList(params);
    }, // Query function
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 2, // Allow retry for transient failures
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    staleTime: 0, // Never consider data fresh - always fetch
    cacheTime: 0, // Never cache data
    refetchOnMount: 'always', // Always refetch on component mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
    enabled: true, // Always enabled for list page
    networkMode: 'always', // Always make network request, ignore cache
    refetchOnReconnect: 'always', // Always refetch when reconnecting
  });
};

/**
 * Custom hook for fetching a single user.
 * It utilizes the `useQuery` hook to retrieve user data by ID.
 *
 * @param {string|number} id - User ID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetUser = (id, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["user-detail", id], // Query key
    queryFn: () => {
      return UserMasterService.get(id);
    }, // Query function
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 2, // Allow retry for transient failures
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    staleTime: 30000, // Consider data fresh for 30 seconds
    cacheTime: 300000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    enabled: !!id, // Only run when ID is provided
  });
};

/**
 * Custom hook for updating user status.
 * It utilizes the `useMutation` hook to update user status.
 *
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateUserStatus = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (request) => {
      return UserMasterService.updateStatus(request);
    }, // Mutation function
    onSuccess: (data) => {
      toast.success(data?.message || "User status updated successfully");
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 1, // Allow one retry for transient failures
    retryDelay: 1000, // 1 second delay
  });
};

/**
 * Custom hook for deleting a user.
 * It utilizes the `useMutation` hook to delete a user.
 *
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useDeleteUser = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (request) => {
      // Accepts a bare UUID or { id, reason }
      return typeof request === "object"
        ? UserMasterService.delete(request.id, request.reason)
        : UserMasterService.delete(request);
    }, // Mutation function
    onSuccess: (data) => {
      toast.success(data?.message || "User deleted successfully");
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 1, // Allow one retry for transient failures
    retryDelay: 1000, // 1 second delay
  });
};

/**
 * Custom hook for restricting a user (read-only mode).
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 * @returns {object} Mutation object from `useMutation`.
 */
const useRestrictUser = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (request) => UserMasterService.restrict(request),
    onSuccess: (data) => {
      toast.success(data?.message || "User restricted successfully");
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
  });
};

/**
 * Custom hook for lifting a user's restriction.
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 * @returns {object} Mutation object from `useMutation`.
 */
const useLiftRestriction = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (id) => UserMasterService.liftRestriction(id),
    onSuccess: (data) => {
      toast.success(data?.message || "Restriction lifted successfully");
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
  });
};

/**
 * Custom hook for fetching admin user details by UUID.
 * It utilizes the `useQuery` hook to retrieve user data with extended profile information.
 *
 * @param {Object} params - Query parameters
 * @param {string} params.uuid - User UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetAdminUserDetails = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["admin-user-detail", params.uuid], // Query key
    queryFn: () => {
      return UserMasterService.getAdminUser(params.uuid);
    }, // Query function
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 2, // Allow retry for transient failures
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    staleTime: 30000, // Consider data fresh for 30 seconds
    cacheTime: 300000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    enabled: !!params.uuid, // Only run when UUID is provided
  });
};

/**
 * Custom hook for granting a bulk "premium reward" (free months of premium) to users.
 * It utilizes the `useMutation` hook to call the grant-premium-reward endpoint.
 *
 * @param {function} onSuccess - Callback to execute when the mutation is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the mutation fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useGrantPremiumReward = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (uuids) => {
      return UserMasterService.grantPremiumReward(uuids);
    }, // Mutation function
    onSuccess: (data) => {
      toast.success(data?.message || "Premium reward granted successfully");
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 0, // Don't auto-retry a bulk grant - avoids double-granting on transient failure
  });
};

export {
  useListUser,
  useGetUser,
  useGetAdminUserDetails,
  useUpdateUserStatus,
  useDeleteUser,
  useRestrictUser,
  useLiftRestriction,
  useGrantPremiumReward,
};
